import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  FormControl,
  CircularProgress,
  Chip
} from '@mui/material';
import { EmployeeAvatar } from '../EmployeeAvatar';
import useEmployees from './hooks/useEmployees';
import { COLORS } from '../../../constants/radar';
import { EMPLOYEE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

/**
 * Reusable employee search component with autocomplete and infinite scrolling
 *
 * @param {Object} props
 * @param {Object|Array} props.value - Currently selected employee(s) - object for single, array for multiple
 * @param {Function} props.onChange - Callback when employee selection changes
 * @param {Boolean} props.multiple - Whether to allow multiple selections (default: false)
 * @param {Boolean} props.includeAllOption - Whether to include an "All Employees" option (ignored when multiple=true)
 * @param {String} props.label - Label for the input field
 * @param {String} props.placeholder - Placeholder text for the input field
 * @param {Number|String} props.width - Width of the component (default: 300px, can be number or string like '100%')
 * @param {String|Number} props.height - Height of the input field (default: '40px')
 * @param {Object} props.sx - Additional styles to apply to the FormControl
 * @param {Array} props.disabledEmployeeIds - Array of employee IDs that should be disabled/not selectable
 * @param {Boolean} props.showSelectedAvatar - Whether to show avatar for the selected employee in the input field (ignored when multiple=true)
 * @param {Boolean} props.hideLabel - Whether to hide the label (useful for DataGrid contexts)
 * @param {String|null} props.returnValue - Return type: EMPLOYEE_RETURN_TYPES values or null for full object (ignored when multiple=true)
 * @param {Boolean} props.disabled - Whether the component should be disabled
 */
const EmployeeSearch = ({
  value,
  onChange,
  multiple = false,
  includeAllOption = false,
  label = "Select Individual",
  placeholder = "Search individuals...",
  width = 300,
  height = '40px',
  sx = {},
  disabledEmployeeIds = [],
  showSelectedAvatar = false,
  hideLabel = false,
  returnValue = null,
  disabled = false
}) => {
  // Disable incompatible features when multiple is true
  const effectiveIncludeAllOption = multiple ? false : includeAllOption;
  const effectiveShowSelectedAvatar = multiple ? false : showSelectedAvatar;
  const effectiveReturnValue = multiple ? null : returnValue;
  const [inputValue, setInputValue] = useState('');
  
  const {
    loading,
    getEmployeeOptions,
    handleScroll,
    handleSearchChange,
    error
  } = useEmployees(effectiveIncludeAllOption, '');

  const handleEmployeeChange = (event, newValue) => {
    if (multiple) {
      // Multi-select: newValue is always an array
      if (onChange) {
        onChange(newValue || []);
      }
    } else {
      // Single-select: existing logic
      if (effectiveIncludeAllOption && newValue && newValue.isAllEmployeesOption) {
        // Reset selection when "All Employees" is selected
        onChange(null);
      } else {
        // Handle return value based on returnValue prop for selector compatibility
        if (onChange) {
          if (!newValue) {
            onChange(null);
          } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.ID) {
            onChange(newValue.id || '');
          } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.EMAIL) {
            onChange(newValue.email || '');
          } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.FULL_NAME) {
            onChange(newValue.fullName || '');
          } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.OBJECT) {
            onChange(newValue);
          } else {
            // Default: return full object
            onChange(newValue);
          }
        }
      }
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    handleSearchChange(newInputValue);
  };

  // Check if an employee is disabled
  const isEmployeeDisabled = (option) => {
    return disabledEmployeeIds.includes(option.id);
  };

  // Calculate avatar size based on height
  const getAvatarSize = () => {
    const heightValue = typeof height === 'string' ? parseInt(height) : height;
    if (heightValue >= 56) return 32;      // Standard Material-UI height
    if (heightValue >= 48) return 28;      // Medium height
    return 24;                             // Compact height (40px and below)
  };

  const avatarSize = getAvatarSize();

  // Find the selected employee object when value is a string (for selector compatibility)
  const selectedEmployee = (() => {
    if (multiple) {
      // For multi-select, value should be an array
      return Array.isArray(value) ? value : [];
    }

    if (!value) {
      return null;
    }

    // If value is already an object, use it directly
    if (typeof value === 'object' && value.id) {
      return value;
    }

    // If value is a string and we have a returnValue prop, find the matching employee
    if (typeof value === 'string' && effectiveReturnValue) {
      const employees = getEmployeeOptions();
      if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.ID) {
        return employees.find(emp => emp.id === value) || null;
      } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.EMAIL) {
        return employees.find(emp => emp.email === value) || null;
      } else if (effectiveReturnValue === EMPLOYEE_RETURN_TYPES.FULL_NAME) {
        return employees.find(emp => emp.fullName === value) || null;
      }
    }

    // Default: assume it's an object or null
    return value;
  })();

  // Handle width - can be number or string
  const widthStyle = typeof width === 'number' ? `${width}px` : width;

  return (
    <FormControl sx={{ width: widthStyle, ...sx }}>
      <Autocomplete
        id="employee-search"
        multiple={multiple}
        options={getEmployeeOptions()}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => {
          // Don't show "All Employees" in the input field
          return option?.isAllEmployeesOption ? '' : (option?.fullName || '');
        }}
        value={selectedEmployee}
        onChange={handleEmployeeChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        clearOnBlur={false}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        slotProps={{
          listbox: {
            onScroll: handleScroll,
            style: { maxHeight: '300px' }
          }
        }}
        getOptionDisabled={isEmployeeDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={hideLabel ? undefined : label}
            placeholder={multiple && selectedEmployee.length > 0 ? '' : placeholder}
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: multiple
                  ? params.InputProps.startAdornment
                  : (effectiveShowSelectedAvatar && selectedEmployee && !selectedEmployee.isAllEmployeesOption ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <EmployeeAvatar employee={selectedEmployee} size={avatarSize} />
                    </Box>
                  ) : undefined),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
              inputLabel: {
                shrink: disabled && !selectedEmployee ? true : undefined
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: multiple ? height : undefined,
                height: multiple ? 'auto' : height,
                backgroundColor: 'white',
                paddingLeft: !multiple && effectiveShowSelectedAvatar && selectedEmployee && !selectedEmployee.isAllEmployeesOption ? '8px' : undefined,
                '& fieldset': {
                  borderColor: '#E2E8F0',
                },
                '&:hover:not(.Mui-disabled) fieldset': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563eb',
                }
              }
            }}
          />
        )}
        renderTags={multiple ? (tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={option.fullName}
                size="small"
                avatar={<EmployeeAvatar employee={option} size={20} />}
                {...tagProps}
                sx={{
                  backgroundColor: '#EFF6FF',
                  color: '#2563EB',
                  '& .MuiChip-avatar': {
                    width: 20,
                    height: 20,
                  },
                  '& .MuiChip-deleteIcon': {
                    color: '#2563EB',
                    '&:hover': {
                      color: '#1d4ed8',
                    },
                  },
                }}
              />
            );
          })
        : undefined}
        sx={{
          '& .MuiAutocomplete-endAdornment': {
            top: multiple ? undefined : '50%',
            transform: multiple ? undefined : 'translateY(-50%)',
            right: '9px'
          },
          '& .MuiAutocomplete-popupIndicator': {
            padding: '2px'
          },
          '& .MuiChip-root': {
            backgroundColor: '#EFF6FF',
            color: '#2563EB',
          }
        }}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          
          // Special rendering for the "All Employees" option
          if (option.isAllEmployeesOption) {
            return (
              <Box
                component="li"
                key={key || option.id}
                {...rest}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: 1, 
                  px: 2,
                  borderBottom: '1px solid #E2E8F0',
                  backgroundColor: '#F8FAFC',
                  color: '#64748B'
                }}
              >
                {option.fullName}
              </Box>
            );
          }
          
          // Regular employee option rendering with disabled styling if needed
          const isDisabled = isEmployeeDisabled(option);
          
          return (
            <Box
              component="li"
              key={key || option.id}
              {...rest}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }}
            >
              <EmployeeAvatar employee={option} size={avatarSize} />
              <Box>
                <Typography sx={{ fontWeight: 500 }}>
                  {option.fullName}
                  {isDisabled && (
                    <Typography 
                      component="span" 
                      sx={{ 
                        color: COLORS.DESTRUCTIVE, 
                        ml: 1, 
                        fontStyle: 'italic',
                        fontWeight: 400,
                        fontSize: '0.875em'
                      }}
                    >
                      • Already assigned
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {option.position?.label}
                  {option.grade && ` • ${option.grade.label}`}
                </Typography>
              </Box>
            </Box>
          );
        }}
        noOptionsText="No individuals found"
      />
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </FormControl>
  );
};

export default EmployeeSearch; 